import { Injectable } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { AlertController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../environments/environment';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root'
})
export class UpdateService {
  private readonly POSTPONED_UPDATE_KEY = 'postponed_update';
  private readonly TUTORIAL_SHOWN_KEY_PREFIX = 'tutorial_shown_';

  constructor(
    private swUpdate: SwUpdate,
    private alertController: AlertController,
    private translate: TranslateService,
    private storageService: StorageService
  ) {
    this.checkForUpdates();
    this.checkPostponedUpdate();
  }

  private async checkForUpdates(): Promise<void> {
    if (this.swUpdate.isEnabled) {
      this.swUpdate.versionUpdates.subscribe(async (evt) => {
        if (evt.type === 'VERSION_READY') {
          const versionEvent = evt as VersionReadyEvent;
          await this.showUpdateAlert();
        }
      });

      // Also check for updates manually
      try {
        await this.swUpdate.checkForUpdate();
      } catch (err) {
        console.error('Failed to check for updates:', err);
      }
    }
  }

  private async checkPostponedUpdate(): Promise<void> {
    const hasPostponedUpdate = await this.storageService.get(this.POSTPONED_UPDATE_KEY) === 'true';
    if (hasPostponedUpdate) {
      await this.showUpdateAlert();
    }
  }

  private async showUpdateAlert(): Promise<void> {
    const alert = await this.alertController.create({
      header: this.translate.instant('update.available'),
      message: this.translate.instant('update.message'),
      buttons: [
        {
          text: this.translate.instant('update.later'),
          role: 'cancel',
          handler: () => {
            this.storageService.set(this.POSTPONED_UPDATE_KEY, 'true');
          }
        },
        {
          text: this.translate.instant('update.now'),
          handler: () => {
            this.storageService.delete(this.POSTPONED_UPDATE_KEY);
            this.activateUpdate();
          }
        }
      ],
      backdropDismiss: true
    });

    await alert.present();
  }

  private async activateUpdate(): Promise<void> {
    try {
      // Reset tutorial setting to show tutorial after update
      // This allows showing changelog or important update information
      await this.storageService.delete(`${this.TUTORIAL_SHOWN_KEY_PREFIX}${environment.appFlavor}`);
      
      await this.swUpdate.activateUpdate();
      window.location.reload();
    } catch (err) {
      console.error('Failed to activate update:', err);
    }
  }
}
